import { useEffect, useState } from "react";
import { API_BASE_URL, AUTH_BASE_URL } from "../props";
import { useSelector } from "react-redux";
import querystring from "querystring";
import { notBlank } from "../util/ext";
import { useMutation, useQueryClient, useQueries } from "@tanstack/react-query";
import { append, concat, find, pickBy, pipe, prop } from "ramda";
import {
  doDeleteWithBody,
  useGet,
  doGetWithNext,
  doPost,
  doPut,
  useDelete,
  usePut,
} from "../common/fetchServices";
import { useUser } from "../user-admin/userServices";
import { useOrganizationDetails } from "../manage-organizations/organizationServices";

const usersUrl = `${AUTH_BASE_URL}/users`;
const orgsUrl = `${AUTH_BASE_URL}/organizations`;
const getAccessControlUrl = id =>
  `${API_BASE_URL}/admin/media/${id}/access-control`;
const getPermissionRoleUrl = ({ id }) =>
  `${API_BASE_URL}/admin/media/${id}/access-control/role`;
const getAccessControlDeleteUrl = ({ mediaPermissionsId }) =>
  `${API_BASE_URL}/admin/media/access-control/${mediaPermissionsId}`;

const getQueryParams = pipe(pickBy(notBlank), querystring.encode);

const buildSearchUrl = (url, params) => {
  const queryParams = getQueryParams(params);
  return `${url}?${queryParams}`;
};

const useSearchQuery = (key, url) => {
  const auth = useSelector(state => state.auth);
  return {
    queryKey: ["org-user-search", key, { url }],
    queryFn: () => doGetWithNext(auth)(url),
  };
};

export const useOrgsAndUserSearch = ({ filter, offset = 0, limit = 50 }) => {
  const userSearchUrl = buildSearchUrl(usersUrl, { filter, offset, limit });
  const orgSearchUrl = buildSearchUrl(orgsUrl, {
    filter,
    offset,
    limit,
  });

  const [orgsAndUsers, setOrgsAndUsers] = useState([]);
  const [hasMore, setHasMore] = useState(false);

  const [usersResults, orgsResults] = useQueries({
    queries: [
      useSearchQuery("users", userSearchUrl),
      useSearchQuery("organizations", orgSearchUrl),
    ],
  });

  useEffect(() => {
    if (usersResults.isSuccess && orgsResults.isSuccess) {
      const data = concat(
        usersResults.data.contents,
        orgsResults.data.contents,
      );
      setOrgsAndUsers(prev => (offset === 0 ? data : [...prev, ...data]));
      setHasMore(usersResults.data.hasMore || orgsResults.data.hasMore);
    }
  }, [
    usersResults.isSuccess,
    usersResults.data,
    orgsResults.isSuccess,
    orgsResults.data,
    offset,
  ]);

  return {
    data: orgsAndUsers,
    hasMore,
    isLoading: usersResults.isLoading && orgsResults.isLoading,
  };
};

export const useMediaPermissions = id => {
  const url = getAccessControlUrl(id);
  return useGet(["access-control", { id }], url);
};

export const useUpdatePermissions = () => {
  const auth = useSelector(state => state.auth);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, accessControl, method }) => {
      const url = getAccessControlUrl(id);

      switch (method) {
        case "PUT":
          return doPut(auth)(url, accessControl);
        case "POST":
          return doPost(auth)(url, accessControl);
        case "DELETE":
          return doDeleteWithBody(auth)(url, accessControl);
        default:
          return;
      }
    },
    onSuccess: (data, variables) => {
      const { id } = variables;
      queryClient.invalidateQueries({ queryKey: ["search"] });
      queryClient.invalidateQueries({ queryKey: ["access-control"] });

      // Modules
      queryClient.invalidateQueries({ queryKey: ["module", { id }] });
      queryClient.invalidateQueries({ queryKey: ["modules-creator"] });
      queryClient.invalidateQueries({ queryKey: ["moduleContent"] });
      queryClient.invalidateQueries({ queryKey: ["module-content-count"] });
      queryClient.invalidateQueries({ queryKey: ["module-content-groups"] });
    },
  });
};

export const useUpdatePermissionRole = () => {
  return usePut(getPermissionRoleUrl);
};

export const useDeletePermission = () => {
  return useDelete(getAccessControlDeleteUrl, {
    queriesToInvalidate: [
      ["access-control", ["id"]],
      ["module", ["id"]],
      ["modules-creator"],
      ["moduleContent"],
      ["module-content-count"],
      ["module-content-groups"],
      ["search"],
    ],
  });
};

export const usePrincipalName = id => {
  const userResults = useUser(id);
  const orgResults = useOrganizationDetails(id);

  const [names, setNames] = useState([]);

  useEffect(() => {
    if (userResults.isSuccess) {
      const name = prop("username", userResults.data);
      setNames(append(name));
    }
  }, [userResults.isSuccess, userResults.data]);

  useEffect(() => {
    if (orgResults.isSuccess) {
      const name = prop("organizationName", orgResults.data);
      setNames(append(name));
    }
  }, [orgResults.isSuccess, orgResults.data]);

  return find(notBlank, names);
};
